
import { useEffect, useState } from "react";
import { Box, Grid, Link, Tooltip, styled, } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { DateFilterOperator, TextFilterOperator } from "components/shared/filters";
import moment from "moment";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { Ico_Delete } from "@sagecentral/core-ui";
import { useWorkItemPermissions } from "hooks/work-items";
import { useFeatureEnabled } from "hooks/shared";

type UseWorkItemDraftColumnsProps = {
  hasViewAllWorkItems: boolean,
  hasViewRelatedWorkItems: boolean,
  openDeleteWorkItem: (workItemId: number, workItemName: string) => void
};

type UseWorkItemDraftColumnsResult = [
  GridColDef[],
  GridColumnVisibilityModel,
  ColumnVisibilityOrOrderChangeHandler,
];

type UseWorkItemDraftColumnsSignature = (props: UseWorkItemDraftColumnsProps) => UseWorkItemDraftColumnsResult;

const LinkStyle = styled(Link)({
  color: '#0066CC',
  fontSize: '14px',
});

const UseWorkItemDraftColumns: UseWorkItemDraftColumnsSignature = (props) => {
  const permissions = useWorkItemPermissions();
  const [ wave4Enabled ] = useFeatureEnabled('wave-4');

  const { hasViewAllWorkItems, hasViewRelatedWorkItems } = props;
  const columnBase: GridColDef[] = [
    {
      field: 'name', headerName: 'Work Item Name', flex: 1, minWidth: 300, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const draftId = params.row.id;
        const workitemEditUrl = `/workitems/create/${draftId}/basic-info`;
        return (
          <>
            <Grid container alignItems="center" style={{ whiteSpace: 'break-spaces', lineHeight: '1.5em', minHeight: '3em' }}>
              <Grid item xs={10}>
              {permissions?.canCreateWorkItem && <LinkStyle href={workitemEditUrl}>
              {name}
              </LinkStyle>}
              {!permissions?.canCreateWorkItem
                  &&
                  <div>
                    {name}
                  </div>}
              </Grid>
            </Grid>
              {/* May be useful when we have notes in drafts 
              {!all && <Grid item xs={2} style={{ textAlign: 'right' }}>
                {unreadNotes > 0 &&
                  <span>
                    <Ico_Bubble style={{ height: '16px' }} />{unreadNotes}
                    {isTagged && isTagged == true && <span style={{ fontWeight: '600', textDecorationThickness: '2px' }}>@</span>}
                  </span>
                }
              </Grid>} */}
          </>
        );
      }
    },
    { field: 'template', headerName: 'Work Item Template', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    { field: 'userName', headerName: 'Created By', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    {
      field: 'dateCreated', headerName: 'Date Created', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter:  value => !value ? null : moment.utc(value).toDate(),
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const dateCreated = params.value;
        return (
          <div>
            {formatDate(dateCreated)}
          </div>
        );
      }
    },
    {
      field: 'dateUpdated', headerName: 'Last Updated', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter:  value => !value ? null : moment.utc(value).toDate(),
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const lastUpdatedDate = params.value;
        return (
          <div>
            {formatDate(lastUpdatedDate)}
          </div>
        );
      }
    },
    {
      field: 'dateExpiration', headerName: 'Expiry Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter:  value => !value ? null : moment.utc(value).toDate(),
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const expirationDate = params.value;
        return (
          <div>
            {formatDate(expirationDate)}
          </div>
        );
      }
    },
    {
      field: 'delete',
      headerName: 'Delete',
      type: 'actions',
      width: 100,
      hideable: false,
      getActions: (params) => {
        return [<GridActionsCellItem
          icon={<Ico_Delete sx={{ color: "#001A69" }} />}
          label="Delete"
          onClick={() => props.openDeleteWorkItem(params.row.id, params.row.name)}
        />];
      }
    }
  ];
  const [columns, setColumns] = useState<GridColDef[]>(columnBase);

  const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns})

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);
    setColumns(newColumns);
  }, [hasViewAllWorkItems, hasViewRelatedWorkItems, wave4Enabled]);

  return [
    columnsModel,
    visibilityModel,
    handleColumnVisibilityOrOrderChange
  ];
};

export default UseWorkItemDraftColumns;